import { Link } from 'react-router';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@xiltrion/ui/sheet';
import { useSession } from '@/app/session';
import { CONFIG_SECTIONS } from '@/features/config/ConfigLayout';
import type { SetupIssue } from './api';
import { SetupChecklist } from './SetupChecklist';

/**
 * Panel con el detalle de lo que no cumple un requisito obligatorio: cada asunto con lo que falta, qué pasaría sin
 * ello y el botón a la pantalla de Configuración que lo resuelve. Se abre desde el aviso del administrador.
 */
export function SetupIssuesSheet({ open, onOpenChange, issues }: { open: boolean; onOpenChange: (open: boolean) => void; issues: SetupIssue[] }) {
  const canAdvanced = useSession().can('admin.advanced');
  // La pantalla que lo resuelve puede ser de configuración avanzada (almacenamiento, motor): sin el permiso, se dice quién la ve.
  const restricted = (path: string) => !canAdvanced && !!CONFIG_SECTIONS.find((s) => path === `/configuracion/${s.to}`)?.advanced;
  const n = issues.reduce((sum, g) => sum + g.checks.length, 0);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Icono nombre="mdi:alert-circle-outline" className="size-4 shrink-0" aria-hidden /> Configuración por revisar</SheetTitle>
          <SheetDescription>{n === 1 ? 'Un requisito obligatorio sin cumplir' : `${n} requisitos obligatorios sin cumplir`}. Cada uno se resuelve en su pantalla de Configuración.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-4 space-y-6">
          {issues.map((g) => (
            <section key={g.key} aria-labelledby={`asunto-${g.key}`} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 id={`asunto-${g.key}`} className="text-sm font-semibold m-0">{g.label}</h3>
                {restricted(g.path)
                  ? <span className="text-xs text-muted-foreground">Lo resuelve quien administra la configuración avanzada</span>
                  : <Button asChild size="sm" variant="outline" onClick={() => onOpenChange(false)}><Link to={g.path}>Abrir en Configuración</Link></Button>}
              </div>
              <SetupChecklist checks={g.checks} />
            </section>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
